
import {
  URL_REG,
  _isNaN,
  isArray,
  isEmpty,
  isIdentity,
  isName,
  isPhone,
  isMailCode,
  isPostalCode,
  isString,
  isValue,
} from './validator'

// 变量类型和对应中文字符串映射关系。
const TYPE_MAP = {
  array: '数组',
  boolean: '布尔值',
  date: '日期',
  float: '小数',
  function: '函数',
  integer: '整数',
  null: 'null',
  number: '数字',
  object: '对象',
  regexp: '正则',
  string: '字符串',
}

/**
 * 获取小数点位数。
 *
 * @ignore
 * @param {number} float=0 - 数字
 * @returns {number} - 小数点位数
 */
function getDecimalDigits (float = 0) {
  if (_isNaN(float)) {
    return 0
  }

  let digit = String(float).split('.')
  digit = digit[1] ? digit[1].length : 0

  return digit
}

/**
 * 拼接类型错误信息。
 *
 * @ignore
 * @param {string} name='' - 目标字符串
 * @param {string} type='' - 类型
 * @returns {string} - 错误信息
 */
function joinTypeErrorMsg (name = '', type = '') {
  return `${name}类型必须为${TYPE_MAP[type]}`
}

/**
 * @description 该函数用于判断如果效验规则为必填，那么value是以下值就算效验不通过。
 * 包括null、undefined、''、[]、{}、如果一个数组全部都是以上数据的组成也空值。
 *
 * @ignore
 * @param {*} value
 * @returns {boolean}
 */
function isEmptyValue (value) {
  return !isValue(value) || isEmpty(value) || (isArray(value) && value.every((item) => isEmptyValue(item)))
}

/**
 * 拼接小数点错误信息。
 *
 * @param {string} name='' - 目标字符串
 * @param {string/number} decimal=0 - 小数点位数
 * @returns {string} - 错误信息
 */
function joinDecimalErrorMsg (name = '', decimal = 0) {
  if (decimal) {
    return `${name}小数点位数不能超过${decimal}位`
  } else {
    return `${name}必须是整数`
  }
}

/**
 * 拼接必填错误信息。
 *
 * @ignore
 * @param {string} name='' - 目标字符串
 * @returns {string} - 错误信息
 */
function joinRequiredErrorMsg (name = '') {
  return `${name}不能为空`
}

/**
 * 拼接格式错误信息。
 *
 * @ignore
 * @param {string} name='' - 目标字符串
 * @returns {string} - 错误信息
 */
function joinFormatErrorMsg (name = '') {
  return `${name}格式不正确`
}

/**
 * 拼接最小值错误信息。
 *
 * @ignore
 * @param {string} name='' - 目标字符串
 * @param {string/number} min=0 - 最小值
 * @returns {string} - 错误信息
 */
function joinMinErrorMsg (name = '', min = 0) {
  return `${name}不能小于${min}`
}

/**
 * 拼接最大值错误信息。
 *
 * @ignore
 * @param {string} name='' - 目标字符串
 * @param {string/number} max=0 - 最大值
 * @returns {string} - 错误信息
 */
function joinMaxErrorMsg (name = '', max = 0) {
  return `${name}不能大于${max}`
}

/**
 * 拼接数字范围错误信息。
 *
 * @ignore
 * @param {string} name - 目标字符串
 * @param {string/number} min=0 - 最小值
 * @param {string/number} max=0 - 最大值
 * @returns {string} - 错误信息
 */
function joinRangeErrorMsg (name = '', min = 0, max = 0) {
  return `${name}取值范围是${min}-${max}`
}

/**
 * 拼接最大长度错误信息。
 *
 * @ignore
 * @param {string} name='' - 目标字符串
 * @param {string/number} maxLength=0 - 最大长度
 * @param {string} unit='位' - 长度单位
 * @returns {string} - 错误信息
 */
function joinMaxLengthErrorMsg (name = '', maxLength = 0, unit = '位') {
  return `${name}长度不能超过${maxLength}${unit}`
}

/**
 * 拼接最小长度错误信息。
 *
 * @ignore
 * @param {string} name='' - 目标字符串
 * @param {string/number} minLength=0 - 最小长度
 * @param {string} unit='位' - 长度单位
 * @returns {string} - 错误信息
 */
function joinMinLengthErrorMsg (name = '', minLength = 0, unit = '位') {
  return `${name}长度不能低于${minLength}${unit}`
}

/**
 * 拼接字符串长度范围错误信息。
 *
 * @ignore
 * @param {string} name - 目标字符串
 * @param {string/number} minLength=0 - 最小长度
 * @param {string/number} maxLength=0 - 最大长度
 * @param {string} unit='位' - 长度单位
 * @returns {string} - 错误信息
 */
function joinRangeLengthErrorMsg (name = '', minLength = 0, maxLength = 0, unit = '位') {
  return `${name}长度范围是${minLength}-${maxLength}${unit}`
}

export class Validator {
  constructor (validator, required = false, trigger = [
    'blur',
    'change',
  ], async = false) {
    this.trigger = trigger

    this.required = required

    if (async) {
      this.asyncValidator = validator
      this.validator = validator
    } else {
      this.validator = validator
    }
  }
}

export class Min extends Validator {
  constructor (name = '', min = 0, decimal = 0) {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (isNaN(value)) {
          errMsg = joinTypeErrorMsg(name, 'number')
        } else {
          const count = getDecimalDigits(value)

          if (count && count > decimal) {
            errMsg = joinDecimalErrorMsg(name, decimal)
          } else if (value < min) {
            errMsg = joinMinErrorMsg(name, min)
          }
        }
      }

      cb(errMsg)
    })
  }
}

export class PostalCode extends Validator {
  constructor (name = '') {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (!isPostalCode(value)) {
          errMsg = joinFormatErrorMsg(name)
        }
      }

      cb(errMsg)
    })
  }
}

export class MailCode extends Validator {
  constructor (name = '') {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (!isMailCode(value)) {
          errMsg = joinFormatErrorMsg(name)
        }
      }

      cb(errMsg)
    })
  }
}

export class Phone extends Validator {
  constructor (name = '', max = 11) {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (!isPhone(value)) {
          errMsg = joinFormatErrorMsg(name)
        } else if (value.length > max) {
          errMsg = joinMaxLengthErrorMsg(name, max)
        }
      }

      cb(errMsg)
    })
  }
}

export class Identity extends Validator {
  constructor (name = '') {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (!isIdentity(value)) {
          errMsg = joinFormatErrorMsg(name)
        }
      }

      cb(errMsg)
    }, false, [
      'blur',
      'change',
    ])
  }
}

export class Name extends Validator {
  constructor (name = '') {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (!isName(value)) {
          errMsg = joinFormatErrorMsg(name)
        }
      }

      cb(errMsg)
    })
  }
}

export class Max extends Validator {
  constructor (name = '', max = Number.MAX_VALUE, decimal = 0) {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (isNaN(value)) {
          errMsg = joinTypeErrorMsg(name, 'number')
        } else {
          const count = getDecimalDigits(value)

          if (count && count > decimal) {
            errMsg = joinDecimalErrorMsg(name, decimal)
          } else if (value > max) {
            errMsg = joinMaxErrorMsg(name, max)
          }
        }
      }

      cb(errMsg)
    })
  }
}

export class Range extends Validator {
  constructor (name = '', min = 0, max = Number.MAX_VALUE, decimal = 0, trigger = [
    'blur',
    'change',
  ]) {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (isNaN(value)) {
          errMsg = joinTypeErrorMsg(name, 'number')
        } else {
          const count = getDecimalDigits(value)

          if (count && count > decimal) {
            errMsg = joinDecimalErrorMsg(name, decimal)
          } else if (value > max || value < min) {
            errMsg = joinRangeErrorMsg(name, min, max)
          }
        }
      }

      cb(errMsg)
    }, false, trigger)
  }
}

export class MaxLength extends Validator {
  constructor (name = '', max = Number.MAX_VALUE) {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (!isString(value)) {
          errMsg = joinTypeErrorMsg(name, 'string')
        } else if (value.length > max) {
          errMsg = joinMaxLengthErrorMsg(name, max)
        }
      }

      cb(errMsg)
    })
  }
}

export class MinLength extends Validator {
  constructor (name = '', min = 0) {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (!isString(value)) {
          errMsg = joinTypeErrorMsg(name, 'string')
        } else if (value.length < min) {
          errMsg = joinMinLengthErrorMsg(name, min)
        }
      }

      cb(errMsg)
    })
  }
}

export class RangeLength extends Validator {
  constructor (name = '', min = 0, max = Number.MAX_VALUE) {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (!isString(value)) {
          errMsg = joinTypeErrorMsg(name, 'string')
        } else if (value.length < min || value.length > max) {
          errMsg = joinRangeLengthErrorMsg(name, min, max)
        }
      }

      cb(errMsg)
    })
  }
}

export class Required extends Validator {
  constructor (name = '') {
    super((rule, value, cb) => {
      let errMsg

      if (isEmptyValue(value)) {
        errMsg = joinRequiredErrorMsg(name)
      } else if (isString(value) && value.trim() === '') {
        errMsg = `${name}不能全部为空格`
      }

      cb(errMsg)
    }, true)
  }
}

export class Remote extends Validator {
  constructor (name = '', apiMethod, key = '', params = {}, trigger = 'blur') {
    super((rule, value) => {
      return new Promise(async (resolve, reject) => {
        const { message, data } = await apiMethod({
          ...params,
          [key]: value,
        })

        if (!data) {
          reject(message)
        } else {
          resolve()
        }
      })
    }, false, trigger, true)
  }
}

export class Type {
  constructor (name = '', type = '') {
    this.type = type

    this.message = joinTypeErrorMsg(name, type)
  }
}

export class Code {
  constructor (name = '') {
    this.pattern = /^[a-zA-Z0-9]+$/g

    this.message = `${name}只允许包含字母和数字`
  }
}
export class QQ {
  constructor (name = '') {
    // eslint-disable-next-line no-useless-escape
    this.pattern = /^[1-9][0-9]{4,14}/g

    this.message = `${name}格式不正确`
  }
}
export class Reg {
  constructor (reg, msg = '字段不符合要求') {
    this.pattern = reg

    this.message = msg
  }
}

export class DynamicReg extends Validator {
  constructor (name = '', key = '', regObj = {}) {
    super((rule, value = '', cb) => {
      let errMsg
      const reg = regObj[key] || {}

      if (reg.regex) {
        value = value || ''
        const result = value.match(new RegExp(reg.regex))

        if (!result) {
          errMsg = reg.message
        }
      }

      cb(errMsg)
    }, false)
  }
}

export class RangeNum extends Validator {
  constructor (name = '', min = 0, max = Number.MAX_VALUE, decimal = 0, trigger = [
    'blur',
    'change',
  ]) {
    super((rule, value, cb) => {
      let errMsg

      if (!isEmptyValue(value)) {
        if (isNaN(value[0]) || isNaN(value[1])) {
          errMsg = joinTypeErrorMsg(name, 'number')
        } else {
          if (value[0] !== null && value[0] !== undefined && value[1] !== null && value[1] !== undefined) {
            if (value[0] > value[1]) {
              errMsg = `${name}范围必须由小到大，请填写正确的${name}范围`
            } else {
              const count0 = getDecimalDigits(value[0])
              const count1 = getDecimalDigits(value[1])

              if ((count0 && count0 > decimal) || (count1 && count1 > decimal)) {
                errMsg = joinDecimalErrorMsg(name, decimal)
              } else if ((value[0] > max || value[0] < min) || (value[1] > max || value[1] < min)) {
                errMsg = joinRangeErrorMsg(name, min, max)
              }
            }
          }
        }
      }

      cb(errMsg)
    })
  }
}

export class Identify {
  constructor (name = '') {
    this.pattern = /^[^\u4e00-\u9fa5]+$/

    this.message = `${name}只允许包含字母、数字和特殊字符`
  }
}

export class Url {
  constructor (name = '') {
    this.pattern = URL_REG
    this.message = `请输入正确的${name}`
  }
}

export const OPERATOR_REG = /(===?|<=|>=|<|>)/g
export const VARIABLE_REG = /[a-zA-Z$_][a-zA-Z\d_]*/g
export const COMPARE_VARIABLE_PREFIX = '___compare___'
export const OPERATOR_MAP = {
  '===': '等于',
  '==': '等于',
  '<=': '小于等于',
  '>=': '大于等于',
  '>': '大于',
  '<': '小于',
}
export class CompareExpression extends Validator {
  constructor (name = '', expression = '', errorMsg = '') {
    super((rule, value = '', cb, { model = {}, renderData = [], validateList = [], trigger }) => {
      const keys = [
        ...renderData.map((item) => item.value),
        ...Object.keys(model),
      ]
      const variableReg = new RegExp(`(${keys.join('|')})`, 'g')
      const variables = this.resolveVariables(expression, model, renderData, variableReg)

      variables.forEach((item) => {
        if (item.name === rule.filed) {
          item.label = name
        }
      })

      if (trigger !== 'force') {
        const other = variables.filter((item) => item.name !== rule.field)

        other.forEach((item) => {
          // 这里做了对移动的兼容
          const vm = validateList.find(({ innerValueName, valueName }) => item.name === innerValueName || item.name === valueName)

          if (vm && (vm.innerValue || vm.value)) {
            vm.validate()
          }
        })
      }

      if (this.variableTypeError(variables)) {
        return cb()
      }

      const handledExpression = this.normalize(expression, variableReg)
      const errMsg = this.execExpression(handledExpression, variables)

      if (errMsg) {
        if (errorMsg) {
          cb(errorMsg)
        } else {
          cb(errMsg)
        }
      } else {
        cb()
      }
    }, false)
  }

  variableTypeError (variables) {
    const yes = variables.some(({ value }) => !isValue(value))

    return yes
  }

  setVariableToGlobal (variables) {
    variables.forEach(({ value, name }) => {
      window[COMPARE_VARIABLE_PREFIX + name] = value
    })
  }

  execExpression (expression, variables) {
    try {
      this.setVariableToGlobal(variables)
      // eslint-disable-next-line no-eval
      const yes = eval(expression)

      if (!yes) {
        return this.makeErrorMsg(expression, variables)
      }
    } catch (error) {
      return error
    }
  }

  normalize (expression, variableReg) {
    return expression.replace(variableReg, (value) => {
      return `${COMPARE_VARIABLE_PREFIX}${value}`
    })
  }

  makeErrorMsg (expression, variables) {
    const operatorName = this.resolveOperatorName(expression)

    return `${variables[0].label}必须${operatorName}${variables[1].label}`
  }

  resolveOperatorName (expression) {
    const operator = expression.match(OPERATOR_REG)

    return OPERATOR_MAP[operator] || ''
  }

  resolveVariables (expression, context, renderData, reg) {
    const variables = expression.match(reg) || []

    return variables.map((item) => {
      const renderDataItem = renderData.find(({ value }) => value === item) || {}

      return {
        name: item,
        value: context[item],

        // 这里的label是对移动的做的兼容
        label: renderDataItem.prefixText || renderDataItem.label || '',
      }
    })
  }
}
